import _ from "@/boot/lodash";

interface AsyncMultiFilterConfig {
  /**
   * Fetch a page of results from the API.
   * Should return an array of raw entities (not yet mapped to { id, label }).
   */
  fetchResults: (params: {
    searchQuery: string;
    offset: number;
    limit: number;
  }) => Promise<any[]>;

  /**
   * Fetch specific entities by their IDs.
   * Should return an array of raw entities matching the given IDs.
   */
  fetchByIds: (ids: string[]) => Promise<any[]>;

  /**
   * Map a raw entity to { id, label } for display.
   */
  mapOption: (entity: any) => { id: string; label: string };

  /**
   * Page size for loadAsyncResults pagination (default: 20).
   */
  pageSize?: number;

  /**
   * Optional hook called before each fetchResults call.
   * Use this to cancel previous in-flight requests (e.g. via vm.cancelRequests()).
   */
  onBeforeFetch?: () => void;
}

/**
 * Creates `loadAsyncResults` and `loadAsyncOptions` functions with:
 * - Shared option cache (id → { id, label })
 * - In-flight request dedup for loadAsyncOptions
 * - Offset-based pagination in loadAsyncResults
 */
export function createAsyncMultiFilter(config: AsyncMultiFilterConfig) {
  const {
    fetchResults,
    fetchByIds,
    mapOption,
    pageSize = 20,
    onBeforeFetch
  } = config;

  // Shared cache of id → { id, label } populated by both functions
  const optionCache = new Map<string, { id: string; label: string }>();

  // In-flight dedup for loadAsyncOptions
  let pendingOptionsRequest: Promise<any[]> | null = null;
  let cachedOptionsKey = "";

  const cacheOptions = (options: { id: string; label: string }[]) => {
    options.forEach(opt => optionCache.set(opt.id, opt));
  };

  const loadAsyncResults = async ({
    searchQuery = "",
    offset = 0,
    callback = null
  }: {
    searchQuery?: string;
    offset?: number;
    callback?: ((error: Error | null, options: any[] | null) => void) | null;
  } = {}) => {
    try {
      if (onBeforeFetch) onBeforeFetch();
      const results = await fetchResults({
        searchQuery,
        offset,
        limit: pageSize
      });

      const options = _.map(results, mapOption);
      cacheOptions(options);

      if (callback) callback(null, options);
    } catch (error) {
      if (callback) callback(error as Error, null);
    }
  };

  const loadAsyncOptions = async (value: string | string[]) => {
    try {
      const idsArray = Array.isArray(value) ? value : [value];

      // Return instantly if all IDs are already cached
      const allCached = idsArray.every(id => optionCache.has(id));
      if (allCached) {
        return idsArray.map(id => optionCache.get(id) as any);
      }

      const ids = idsArray.sort().join(",");

      // Reuse in-flight request for the same set of IDs
      if (ids === cachedOptionsKey && pendingOptionsRequest) {
        return pendingOptionsRequest;
      }

      // Only fetch IDs not already in cache
      const uncachedIds = idsArray.filter(id => !optionCache.has(id));

      cachedOptionsKey = ids;
      pendingOptionsRequest = fetchByIds(uncachedIds).then(results => {
        const fetched = _.map(results, mapOption);
        cacheOptions(fetched);
        // Return ALL requested options (cached + freshly fetched)
        return idsArray.map(id => optionCache.get(id) || { id, label: id });
      });

      const result = await pendingOptionsRequest;
      return result;
    } catch (error) {
      pendingOptionsRequest = null;
      console.error(error);
      return [];
    }
  };

  return { loadAsyncResults, loadAsyncOptions };
}
